import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Mail, MessageSquare, Lock } from "lucide-react";
import Navbar from "../components/Navbar";
import api from "../lib/api";

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isOwner, setIsOwner] = useState(true);
  const [defaults, setDefaults] = useState({ email: "", sms: "" });
  const [emailMsg, setEmailMsg] = useState("");
  const [smsMsg, setSmsMsg] = useState("");

  useEffect(() => {
    api.get("/message-settings").then((r) => {
      setIsOwner(!!r.data.is_owner);
      setDefaults({ email: r.data.default_email_message, sms: r.data.default_sms_message });
      setEmailMsg(r.data.email_message || r.data.default_email_message);
      setSmsMsg(r.data.sms_message || r.data.default_sms_message);
    }).catch(() => toast.error("Failed to load settings")).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/message-settings", { email_message: emailMsg, sms_message: smsMsg });
      toast.success("Message defaults saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const resetEmail = () => setEmailMsg(defaults.email);
  const resetSms = () => setSmsMsg(defaults.sms);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="p-16 flex justify-center">
          <div className="h-8 w-8 rounded-full border-2 border-[#E2E8F0] border-t-[#2563EB] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-5 py-8" data-testid="settings-page">
        <h1 className="text-2xl font-extrabold tracking-tight text-[#0F172A]">Message defaults</h1>
        <p className="text-sm text-[#64748B] mt-1">Set the default wording customers see when you send a signing link. You can still tweak the text right before each send.</p>

        <div className="mt-4 rounded-lg bg-[#EFF6FF] px-4 py-3 text-xs text-[#1E40AF] leading-relaxed">
          Use placeholders and we'll fill them in automatically:
          <span className="font-mono font-semibold"> {"{business}"} {"{amount}"} {"{customer}"} {"{frequency}"}</span>.
          The secure signing link, button and legal footer are always added for you.
        </div>

        {!isOwner && (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#B45309]" data-testid="settings-readonly-banner">
            <Lock size={15} /> Only the vault owner can change the team's message defaults.
          </div>
        )}

        <fieldset disabled={!isOwner} className="mt-6 space-y-6">
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-[#2563EB]" />
                <h3 className="text-sm font-bold text-[#0F172A]">Email message</h3>
              </div>
              <button type="button" onClick={resetEmail} data-testid="reset-email-msg-btn" className="text-xs font-semibold text-[#64748B] hover:text-[#2563EB] transition-colors disabled:opacity-40">
                Reset to default
              </button>
            </div>
            <textarea data-testid="settings-email-msg-input" value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} rows={4}
              className="mt-3 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm outline-none focus:border-[#2563EB] resize-none disabled:bg-slate-50" />
          </div>

          <div className="rounded-xl border border-[#E2E8F0] bg-white p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare size={16} className="text-[#2563EB]" />
                <h3 className="text-sm font-bold text-[#0F172A]">SMS message</h3>
              </div>
              <button type="button" onClick={resetSms} data-testid="reset-sms-msg-btn" className="text-xs font-semibold text-[#64748B] hover:text-[#2563EB] transition-colors disabled:opacity-40">
                Reset to default
              </button>
            </div>
            <textarea data-testid="settings-sms-msg-input" value={smsMsg} onChange={(e) => setSmsMsg(e.target.value)} rows={3}
              className="mt-3 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm outline-none focus:border-[#2563EB] resize-none disabled:bg-slate-50" />
            <p className="text-xs text-[#94A3B8] mt-1.5">Keep it short — the signing link is appended automatically.</p>
          </div>

          {isOwner && (
            <button onClick={save} data-testid="settings-save-btn" disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] transition-all active:scale-[0.99] disabled:opacity-60">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save defaults
            </button>
          )}
        </fieldset>
      </main>
    </div>
  );
}
